<script module lang="ts">
	export type Rejection = {
		errors: { message: string; code: string }[];
		file: File;
	};

	export const getSupportedFormats = (accept: string[] | undefined) =>
		(accept ?? []).reduce((accumulator, mimeType) => {
			const extension = mimeType.split('/')?.[1]?.toUpperCase();
			if (extension) {
				return `${accumulator}${!accumulator ? `.${extension}` : `, .${extension}`}`;
			}

			return accumulator;
		}, '');
</script>

<script lang="ts">
	import classNames from 'classnames';
	import Dropzone from 'svelte-file-dropzone/Dropzone.svelte';
	import { twMerge } from 'tailwind-merge';
	import { createDialogStores, type DialogStores } from '../Dialog';
	import { Txt } from '../Txt';
	import { DropzoneContent } from './DropzoneContent';
	import { DropzoneFiles } from './DropzoneFiles';
	import { DropzoneUrlDialog } from './DropzoneUrlDialog';
	import { getSupportedFormatsWithParentheses } from './DropzoneUrlDialog/utils/getSupportedFormatsWithParentheses/getSupportedFormatsWithParentheses';
	import type { DropzoneFile } from './types';

	let {
		enableUrlDialog = false,
		scrollableFiles = false,
		showFiles = true,
		classes = {},
		maxSize = 20000000,
		multiple = undefined,
		accept,
		files = $bindable([]),
		dataCy,
		id = undefined,
		supportedFormatsOverride = undefined,
		supportedFormatsWithParenthesesOverride = undefined,
		dialogStores = createDialogStores(),
		onSubmitFiles = undefined,
		onSubmitUrl = undefined,
		onRemoveFiles = undefined,
		custom,
		helper,
		...rest
	}: Props = $props();

	interface Props {
		scrollableFiles?: boolean;
		enableUrlDialog?: boolean;
		showFiles?: boolean;
		classes?: { container?: string; inner?: string };
		maxSize?: number;
		multiple?: boolean | undefined;
		accept: string[];
		class?: string;
		files?: DropzoneFile[];
		dataCy: string;
		id?: string | undefined;
		supportedFormatsOverride?: string | undefined;
		supportedFormatsWithParenthesesOverride?: string | undefined;
		dialogStores?: DialogStores;
		onSubmitFiles?: ((files: File[]) => void) | undefined;
		onSubmitUrl?: ((url: string) => void) | undefined;
		onRemoveFiles?: (() => void) | undefined;
		custom?: import('svelte').Snippet;
		helper?: import('svelte').Snippet;
	}

	let rejections: Rejection[] = $state([]);

	const handleFilesRemove = (filename: string) => {
		if (onRemoveFiles) {
			onRemoveFiles();
		}
		files = files.filter((file) => file.name !== filename);
	};

	const handleSubmitLink = (url: string) => {
		if (multiple) {
			files = [...files, { url, name: url }];
		} else {
			files = [{ url, name: url }];
		}

		if (onSubmitUrl) {
			onSubmitUrl(url);
		}
	};

	const handleFilesSelect = (e: {
		detail: {
			acceptedFiles: File[];
			fileRejections: Rejection[];
		};
	}) => {
		const { acceptedFiles, fileRejections } = e.detail;

		rejections = fileRejections;

		if (acceptedFiles.length) {
			if (multiple) {
				files = [...files, ...acceptedFiles];
			} else {
				files = [acceptedFiles?.[0]];
			}

			if (onSubmitFiles) {
				if (multiple) {
					onSubmitFiles(acceptedFiles);
				} else {
					onSubmitFiles([acceptedFiles?.[0]]);
				}
			}
		}
	};

	let supportedFormats = $derived(getSupportedFormats(accept));
	let supportedFormatsWithParentheses = $derived(getSupportedFormatsWithParentheses(accept));
	let dataCyPrefix = $derived(`${dataCy}-dropzone`);
</script>

<div class={classes.container} data-cy={dataCyPrefix} {id}>
	<Dropzone
		inputElement={undefined}
		on:drop={handleFilesSelect}
		{accept}
		{maxSize}
		{multiple}
		containerClasses={twMerge(
			'!py-8 !px-2 !border border-gray-175 !rounded !border-dashed !bg-gray-50',
			classes.inner
		)}
		{...rest}
	>
		{#if custom}
			{@render custom()}
		{:else}
			<DropzoneContent
				{dialogStores}
				{enableUrlDialog}
				supportedFormats={supportedFormatsOverride || supportedFormats}
				{dataCy}
			/>
		{/if}
	</Dropzone>

	{#if helper}
		{@render helper()}
	{:else}
		<Txt variant="body3" class="mt-1 text-gray-400">
			Max file size is {maxSize / 1000000}MB
		</Txt>
	{/if}

	{#each rejections as { file, errors }, index (file.name)}
		<Txt variant="body3" class={`${!index ? 'mt-2' : 'mt-1'} text-red-500`}>
			{#if errors[0].code === 'file-too-large'}
				{`The file "${file.name}" cannot be uploaded because it exceeds the size limit. Please ensure the file size is within the permissible range.`}
			{:else if errors[0].code === 'file-invalid-type'}
				{`The file "${file.name}" cannot be uploaded. Accepted file types are: ${supportedFormatsWithParenthesesOverride || supportedFormatsWithParentheses} only.`}
			{:else}
				{`The file "${file.name}" cannot be uploaded for the following reason : ${errors[0].message}.`}
			{/if}
		</Txt>
	{/each}

	{#if showFiles}
		<DropzoneFiles
			dataCy={dataCyPrefix}
			bind:files
			onRemoveFile={handleFilesRemove}
			class={classNames({
				'mt-2 pb-4 border border-gray-175 max-h-[246px] overflow-scroll px-4':
					files.length && scrollableFiles
			})}
		/>
	{/if}
</div>

{#if enableUrlDialog}
	<DropzoneUrlDialog {accept} {maxSize} onSubmit={handleSubmitLink} {dataCy} {dialogStores} />
{/if}
